// Reusing a single Intl.NumberFormat per `maxDecimals` is cheaper than calling
// Number.prototype.toLocaleString on each value (which may build a formatter
// every call). en-AU + no grouping matches formatNumberWithCommas and keeps
// large numbers separator-free.
const formatterCache = new Map<number, Intl.NumberFormat>();

const getFormatter = (maxDecimals: number): Intl.NumberFormat => {
  let formatter = formatterCache.get(maxDecimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
      useGrouping: false,
    });
    formatterCache.set(maxDecimals, formatter);
  }
  return formatter;
};

/**
 * Formats a numeric value to at most `maxDecimals` decimal places, without
 * padding with trailing zeros. Accepts both numbers and numeric strings.
 *
 *   formatToMaxDecimals(1.23456)    // "1.235"
 *   formatToMaxDecimals("1.23456")  // "1.235"
 *   formatToMaxDecimals(1.23)       // 1.23   (unchanged, not "1.230")
 *
 * Values that aren't finite numbers (booleans, objects, non-numeric strings,
 * empty strings) are returned untouched. Values that already have
 * `maxDecimals` or fewer decimals are also returned as-is, so integers and
 * ids keep their exact original representation.
 */
export const formatToMaxDecimals = <T>(
  value: T,
  maxDecimals = 3,
): T | string => {
  if (typeof value !== "number" && typeof value !== "string") {
    return value;
  }

  const trimmed = typeof value === "string" ? value.trim() : value;
  if (trimmed === "") {
    return value;
  }

  const num = Number(trimmed);
  if (!Number.isFinite(num)) {
    return value;
  }

  const decimals = String(trimmed).split(".")[1] ?? "";
  if (decimals.length <= maxDecimals) {
    return value;
  }

  // Round to the max decimals without padding trailing zeros, guarding against
  // a value that rounds to a stray "-0".
  const formatted = getFormatter(maxDecimals).format(num);
  return formatted === "-0" ? "0" : formatted;
};
